import {
  Plus,
  AtSign,
  Paperclip,
  CircleCheck,
  Check,
  ChevronDown,
  X,
  Mail,
  Menu as MenuIcon,
  Link as LinkIcon,
  Repeat,
  Clock,
  Eye,
} from "lucide-react";
import {
  Heading,
  Stat,
  Button,
  Flex,
  Link,
  Stack,
  Text,
  Spinner,
  Table,
  Badge,
  Menu,
  IconButton,
  Alert,
  ButtonGroup,
} from "@chakra-ui/react";
import { useSession } from "../../hooks/useSession";
import { useInvoiceList } from "../../hooks/useInvoiceList";
import { useInvoiceSend } from "../../hooks/useInvoiceSend";
import { useInvoicesQueue } from "../../hooks/useInvoicesQueue";
import { useInvoiceUpdate } from "../../hooks/useInvoiceUpdate";
import { useDuplicateInvoice } from "../../hooks/useDuplicateInvoice";
import { InvoiceDeliveryStatus, InvoiceStatus, OpenAPI } from "../../generated";
import { getColorScheme } from "../../utils/colorScheme";
import { Link as ReactRouterLink } from "react-router-dom";

export default function InvoicePage() {
  const sessionQuery = useSession();

  const { data, isLoading, hasNextPage, fetchNextPage } = useInvoiceList();

  const invoicesQueueMutation = useInvoicesQueue();
  const invoiceSendMutation = useInvoiceSend();
  const invoiceUpdateMutation = useInvoiceUpdate();
  const duplicateInvoiceMutation = useDuplicateInvoice();

  const flattenedData = data?.pages.flatMap((page) => page);

  return (
    <Stack>
      <Heading as="h1" mb={4}>
        Fakturaer
      </Heading>
      {!isLoading && !sessionQuery.data?.isProfileCompleted && (
        <Alert.Root>
          <Stack direction="row" alignItems="center">
            <Text>Fullfør profilen før du kan lage fakturaer</Text>
            <Link asChild>
              <ReactRouterLink to="/profile/">
                <Button>Gå til profil</Button>
              </ReactRouterLink>
            </Link>
          </Stack>
        </Alert.Root>
      )}
      {flattenedData?.find(
        (invoice) => invoice.status === InvoiceStatus.DRAFT,
      ) && (
        <Alert.Root status="info">
          <Alert.Indicator />
          Du har fakturaer som utkast. Klikk &quot;Registrer og send
          faktura&quot; for å generere fakturanummer og legge alle fakturaene
          til utsending.
        </Alert.Root>
      )}
      <Stack direction="row" flexWrap="wrap">
        <ButtonGroup>
          <Link asChild>
            <ReactRouterLink to="/invoices/create/">
              <Button
                variant="solid"
                colorPalette="blue"
                disabled={!sessionQuery.data?.isProfileCompleted}
              >
                Lag ny <Plus />
              </Button>
            </ReactRouterLink>
          </Link>
          <Link asChild>
            <ReactRouterLink to="/invoices/templates/">
              <Button
                variant="solid"
                colorPalette="purple"
                disabled={!sessionQuery.data?.isProfileCompleted}
              >
                Maler <Paperclip />
              </Button>
            </ReactRouterLink>
          </Link>
          <Link asChild>
            <ReactRouterLink to="/invoices/create">
              <Button variant="solid" disabled={true}>
                Fast oppdrag <Repeat />
              </Button>
            </ReactRouterLink>
          </Link>
          <Link asChild>
            <ReactRouterLink to="/invoices/create">
              <Button variant="solid" disabled={true}>
                Eksporter
              </Button>
            </ReactRouterLink>
          </Link>
          <Link asChild>
            <ReactRouterLink to="/invoices/create">
              <Button variant="solid" disabled={true}>
                Del oversikt <LinkIcon />
              </Button>
            </ReactRouterLink>
          </Link>
        </ButtonGroup>
      </Stack>
      <Flex alignItems="center" wrap="wrap">
        <Stat.Root my={4}>
          <Stat.Label>Antall sendte fakturaer</Stat.Label>
          <Stat.ValueText>{flattenedData?.length}</Stat.ValueText>
          <Stat.HelpText>
            <Stat.UpIndicator />
            {flattenedData?.[flattenedData?.length - 1]?.issueDate} -{" "}
            {flattenedData?.[0]?.issueDate}
          </Stat.HelpText>
        </Stat.Root>
        <Stack direction="row" gap={4} wrap="wrap">
          <Button
            onClick={() => invoicesQueueMutation.mutate(undefined)}
            disabled={
              !flattenedData?.some((x) => x.status === InvoiceStatus.DRAFT)
            }
          >
            Registrer og send faktura
            {invoicesQueueMutation.isPending ? <Spinner /> : <Clock />}
          </Button>
          {sessionQuery.data?.isStaff && (
            <Button
              onClick={() => invoiceSendMutation.mutate()}
              disabled={
                !flattenedData?.some(
                  (x) => x.status === InvoiceStatus.REGISTERED,
                )
              }
            >
              Send fakturaer
              {invoiceSendMutation.isPending ? <Spinner /> : <Mail />}
            </Button>
          )}
        </Stack>
      </Flex>
      <Table.ScrollArea>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Fakturanummer</Table.ColumnHeader>
              <Table.ColumnHeader>Kunde</Table.ColumnHeader>
              <Table.ColumnHeader>Fakturadato</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Handlinger</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!isLoading &&
              flattenedData?.map((invoice) => (
                <Table.Row key={invoice.uuid}>
                  <Table.Cell>{invoice.invoiceNumber ?? <AtSign />}</Table.Cell>
                  <Table.Cell>{invoice.customerName}</Table.Cell>
                  <Table.Cell>{invoice.issueDate}</Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette={getColorScheme(invoice.status)}>
                      <Stack direction="row" alignItems="center">
                        <Text fontSize={14}>{invoice.status}</Text>
                        {invoice.status === InvoiceStatus.PAID ? (
                          <Check size={14} />
                        ) : invoice.deliveryStatus ===
                            InvoiceDeliveryStatus.SENT &&
                          new Date(invoice.dueDate) < new Date() ? (
                          <X size={14} />
                        ) : invoice.deliveryStatus ===
                          InvoiceDeliveryStatus.SENT ? (
                          <Mail size={14} />
                        ) : invoice.deliveryStatus ===
                            InvoiceDeliveryStatus.QUEUED ||
                          invoice.status === InvoiceStatus.REGISTERED ? (
                          <Clock size={14} />
                        ) : (
                          ""
                        )}
                      </Stack>
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Stack direction="row">
                      <Link asChild>
                        <ReactRouterLink
                          to={`/invoices/${
                            invoice.invoiceNumber || invoice.uuid
                          }/`}
                        >
                          <IconButton
                            colorPalette="blackAlpha"
                            aria-label="Se detaljer"
                          >
                            <Eye />
                          </IconButton>
                        </ReactRouterLink>
                      </Link>
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <IconButton colorPalette="blue">
                            <MenuIcon />
                          </IconButton>
                        </Menu.Trigger>
                        <Menu.Positioner>
                          <Menu.Content>
                            <Menu.Item value="details">
                              <Link asChild>
                                <ReactRouterLink
                                  to={`/invoices/${
                                    invoice.invoiceNumber || invoice.uuid
                                  }/`}
                                >
                                  Se detaljer
                                </ReactRouterLink>
                              </Link>
                            </Menu.Item>
                            <Menu.Item value="pdf">
                              <Link
                                href={`${OpenAPI.BASE}/invoices/${invoice.uuid}/pdf/`}
                                target="_blank"
                              >
                                Vis PDF
                              </Link>
                            </Menu.Item>
                            <Menu.Item
                              value="duplicate"
                              onClick={() =>
                                duplicateInvoiceMutation.mutate(invoice.uuid)
                              }
                            >
                              Dupliser
                            </Menu.Item>
                            {invoice.status != InvoiceStatus.PAID && (
                              <Menu.Item
                                value="paid"
                                onClick={() =>
                                  invoiceUpdateMutation.mutate({
                                    invoiceUuid: invoice.uuid,
                                    requestBody: {
                                      status: InvoiceStatus.PAID,
                                    },
                                  })
                                }
                              >
                                Sett til betalt <CircleCheck size={14} />
                              </Menu.Item>
                            )}
                          </Menu.Content>
                        </Menu.Positioner>
                      </Menu.Root>
                    </Stack>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
      {isLoading && (
        <Flex flex={1} justifyContent="center">
          <Spinner />
        </Flex>
      )}
      <Button
        disabled={!hasNextPage}
        onClick={() => fetchNextPage()}
      >
        Hent flere <ChevronDown />
      </Button>
    </Stack>
  );
}
